import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, startEmailCode, verifyEmailCode } from '../lib/api';

interface EmailSignInProps {
  reason: string;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = 'enter-email' | 'enter-code';

export function EmailSignIn({ reason, onClose, onSuccess }: EmailSignInProps) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('enter-email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (step === 'enter-code') {
      // Tiny delay so the bottom-sheet animation doesn't fight the keyboard.
      const t = setTimeout(() => codeInputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [step]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const sendCode = async () => {
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const r = await startEmailCode(email.trim());
      setStep('enter-code');
      setResendIn(30);
      if (r.devCode) {
        setInfo(`Dev mode — code is ${r.devCode}`);
      } else if (!r.delivered) {
        setInfo('Code generated, but email delivery failed. Check the server log.');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send code.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    void sendCode();
  };

  const submitCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (submitting || code.length !== 6) return;
    setError(null);
    setSubmitting(true);
    try {
      await verifyEmailCode(email.trim(), code);
      // Refresh anything that depends on auth state.
      await qc.invalidateQueries();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not verify code.');
      setCode('');
      // Re-focus so the user can retype immediately.
      setTimeout(() => codeInputRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(2px)' }} />
      <div
        style={{
          position: 'relative',
          background: 'var(--color-bg)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '8px 20px max(20px, env(safe-area-inset-bottom))',
          boxShadow: 'var(--shadow-sheet)',
        }}
      >
        <div aria-hidden style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--color-divider)', margin: '8px auto 16px' }} />

        {step === 'enter-email' ? (
          <form onSubmit={submitEmail}>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>Sign in to continue</h2>
            <p style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>{reason}</p>

            <label style={{ display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-secondary)', marginTop: 20, marginBottom: 6 }}>
              Email
            </label>
            <input
              autoFocus
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: 12,
                border: '1px solid var(--color-divider)',
                background: 'var(--color-surface)',
                color: 'var(--color-primary)',
                fontSize: 16,
              }}
            />

            {error && <p role="alert" style={{ fontSize: 13, marginTop: 10, color: 'var(--color-danger)' }}>{error}</p>}

            <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: 16 }} disabled={submitting || !email.trim()}>
              {submitting ? 'Sending…' : 'Send me a code'}
            </button>

            <p style={{ fontSize: 11, textAlign: 'center', marginTop: 12, color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
              We'll only use your email to sign you in. No marketing.
            </p>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>Enter your code</h2>
            <p style={{ fontSize: 14, marginTop: 8, lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
              We sent a 6-digit code to <strong style={{ color: 'var(--color-primary)' }}>{email}</strong>. It expires in 10 minutes.
            </p>

            {info && (
              <p style={{ fontSize: 12, marginTop: 10, padding: '8px 12px', borderRadius: 10, background: 'var(--color-surface)', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                {info}
              </p>
            )}

            <label style={{ display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-secondary)', marginTop: 20, marginBottom: 6 }}>
              6-digit code
            </label>
            <input
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              placeholder="••••••"
              value={code}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(v);
                if (v.length === 6) {
                  // Auto-submit when 6 digits entered.
                  setTimeout(() => submitCode(), 0);
                }
              }}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: 12,
                border: '1px solid var(--color-divider)',
                background: 'var(--color-surface)',
                color: 'var(--color-primary)',
                fontFamily: 'var(--font-mono)',
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: '0.4em',
                textAlign: 'center',
              }}
            />

            {error && <p role="alert" style={{ fontSize: 13, marginTop: 10, color: 'var(--color-danger)' }}>{error}</p>}

            <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: 16 }} disabled={submitting || code.length !== 6}>
              {submitting ? 'Verifying…' : 'Sign in'}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
              <button
                type="button"
                onClick={() => {
                  setStep('enter-email');
                  setCode('');
                  setError(null);
                  setInfo(null);
                }}
                style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', fontSize: 13, padding: '4px 0' }}
              >
                Use a different email
              </button>
              <button
                type="button"
                disabled={resendIn > 0 || submitting}
                onClick={() => sendCode()}
                style={{ background: 'transparent', border: 'none', color: resendIn > 0 ? 'var(--color-text-muted)' : 'var(--color-accent)', fontSize: 13, fontWeight: 500, padding: '4px 0' }}
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
